import React, { useState, useEffect, useContext, useReducer } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import holidayService from "../../services/holidayService";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_HOLIDAYS":
      return action.payload;
    case "UPDATE_HOLIDAYS":
      const holiday = action.payload;
      const holidayIndex = state.findIndex((s) => s.id === holiday.id);

      if (holidayIndex !== -1) {
        state[holidayIndex] = holiday;
        return [...state];
      } else {
        return [holiday, ...state];
      }
    case "DELETE_HOLIDAYS":
      const holidayId = action.payload;
      return state.filter((holiday) => holiday.id !== holidayId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 400,
  },
  infoText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const Holidays = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [holidays, dispatch] = useReducer(reducer, []);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  // Form state
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    dispatch({ type: "RESET" });
    fetchHolidays();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on(`company${user.companyId}-holiday`, (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_HOLIDAYS", payload: data.holiday });
        } else if (data.action === "delete") {
          dispatch({ type: "DELETE_HOLIDAYS", payload: data.holidayId });
        }
      });

      return () => {
        socket.off(`company${user.companyId}-holiday`);
      };
    }
  }, [socket, user]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const { data } = await holidayService.list();
      dispatch({ type: "LOAD_HOLIDAYS", payload: data.holidays });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (holiday = null) => {
    if (holiday) {
      setSelectedHoliday(holiday);
      setDay(holiday.day.toString());
      setMonth(holiday.month.toString());
      setDescription(holiday.description || "");
      setIsActive(holiday.isActive);
    } else {
      setSelectedHoliday(null);
      setDay("");
      setMonth("");
      setDescription("");
      setIsActive(true);
    }
    setHolidayModalOpen(true);
  };

  const handleCloseModal = () => {
    setHolidayModalOpen(false);
    setSelectedHoliday(null);
    setDay("");
    setMonth("");
    setDescription("");
    setIsActive(true);
  };

  const handleSave = async () => {
    try {
      const holidayData = {
        day: parseInt(day),
        month: parseInt(month),
        description,
        isActive,
      };

      if (selectedHoliday) {
        await holidayService.update(selectedHoliday.id, holidayData);
        toast.success(i18n.t("holidays.toasts.updated"));
      } else {
        await holidayService.create(holidayData);
        toast.success(i18n.t("holidays.toasts.created"));
      }

      handleCloseModal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (holidayId) => {
    try {
      await holidayService.remove(holidayId);
      toast.success(i18n.t("holidays.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingHoliday(null);
  };

  const getDaysInMonth = (month) => {
    if (!month) return 31;
    const monthNum = parseInt(month);
    return new Date(2000, monthNum, 0).getDate(); // Ano bissexto não importa
  };

  const maxDay = getDaysInMonth(month);

  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const days = Array.from({ length: maxDay }, (_, i) => i + 1);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("holidays.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
            startIcon={<AddIcon />}
          >
            {i18n.t("holidays.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <p className={classes.infoText}>
          {i18n.t("holidays.infoText")}
        </p>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("holidays.table.dayMonth")}</TableCell>
              <TableCell>{i18n.t("holidays.table.description")}</TableCell>
              <TableCell align="center">{i18n.t("holidays.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("holidays.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && holidays.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {i18n.t("holidays.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              holidays.map((holiday) => (
                <TableRow key={holiday.id}>
                  <TableCell align="center">
                    {holiday.day.toString().padStart(2, "0")}/
                    {holiday.month.toString().padStart(2, "0")}
                  </TableCell>
                  <TableCell>{holiday.description || "-"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={holiday.isActive ? i18n.t("holidays.table.statusActive") : i18n.t("holidays.table.statusInactive")}
                      color={holiday.isActive ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(holiday)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingHoliday(holiday);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={holidayModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedHoliday ? i18n.t("holidays.modal.editTitle") : i18n.t("holidays.modal.createTitle")}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <p className={classes.infoText}>
            {i18n.t("holidays.modal.infoText")}
          </p>

          <FormControl className={classes.formControl} fullWidth>
            <InputLabel>{i18n.t("holidays.modal.month")}</InputLabel>
            <Select
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                // Ajustar dia se necessário
                const newMaxDay = getDaysInMonth(e.target.value);
                if (day && parseInt(day) > newMaxDay) {
                  setDay(newMaxDay.toString());
                }
              }}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.formControl} fullWidth>
            <InputLabel>{i18n.t("holidays.modal.day")}</InputLabel>
            <Select
              value={day}
              onChange={(e) => setDay(e.target.value)}
              disabled={!month}
            >
              {days.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={i18n.t("holidays.modal.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder={i18n.t("holidays.modal.descriptionPlaceholder")}
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("holidays.modal.isActive")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {i18n.t("holidays.modal.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!day || !month || !description.trim()}
          >
            {selectedHoliday ? i18n.t("holidays.modal.save") : i18n.t("holidays.modal.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("holidays.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingHoliday(null);
        }}
        onConfirm={() => handleDelete(deletingHoliday?.id)}
      >
        {deletingHoliday && (
          <p>
            {i18n.t("holidays.confirmationModal.deleteMessage")}{" "}
            {deletingHoliday.day.toString().padStart(2, "0")}/
            {deletingHoliday.month.toString().padStart(2, "0")}?
          </p>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Holidays;

